import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Piece, PieceType } from './entities/Piece';
import { Grid } from './systems/Grid';
import { PieceGenerator } from './systems/PieceGenerator';

export class BlockDropGame extends BaseGame {
  private grid: Grid;
  private pieceGenerator: PieceGenerator;
  private currentPiece: Piece | null = null;
  private nextPiece: Piece | null = null;
  
  // Game timing
  private dropTimer: number = 0;
  private dropInterval: number = 1.0; // Start with 1 second per drop
  private softDropTimer: number = 0;
  private lockDelayTimer: number = 0;
  private readonly LOCK_DELAY_DURATION: number = 0.5; // 500ms lock delay
  
  // Input handling
  private keyRepeatTimers: { [key: string]: number } = {};
  private readonly KEY_REPEAT_DELAY: number = 0.15; // 150ms repeat delay
  private readonly SOFT_DROP_SPEED: number = 0.05; // 20x faster when soft dropping
  
  // Game constants
  private readonly GRID_WIDTH: number = 12; // Wider grid for more screen usage
  private readonly GRID_HEIGHT: number = 20;
  private BLOCK_SIZE: number = 28; // Will be calculated dynamically
  private GRID_OFFSET_X: number = 146; // Will be calculated dynamically
  private GRID_OFFSET_Y: number = 20; // Will be calculated dynamically
  
  // Scoring (Nintendo NES Tetris authentic scoring)
  private lines: number = 0;
  private readonly LINE_CLEAR_BASE_POINTS = {
    SINGLE: 40,
    DOUBLE: 100,
    TRIPLE: 300,
    TETRIS: 1200
  };
  private readonly DROP_POINTS = {
    SOFT_DROP: 1,
    HARD_DROP: 2
  };
  
  // Statistics tracking (like original NES Tetris)
  private pieceStats = {
    [PieceType.I]: 0,
    [PieceType.O]: 0,
    [PieceType.T]: 0,
    [PieceType.S]: 0,
    [PieceType.Z]: 0,
    [PieceType.J]: 0,
    [PieceType.L]: 0
  };

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 600, height: 900, fps: 60, scale: 1 }; // Portrait for mobile
    super(renderManager, audioManager, gameConfig, 'block-drop');

    this.grid = new Grid(this.GRID_WIDTH, this.GRID_HEIGHT, renderManager);
    this.pieceGenerator = new PieceGenerator(renderManager, audioManager);

    // Calculate responsive sizing
    this.calculateResponsiveSizing();
  }

  private calculateResponsiveSizing(): void {
    const canvas = this.renderManager.getCanvas();
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Use FULL screen - UI will overlay on top/bottom
    const uiTopSpace = 0; // UI overlays on top
    const uiBottomSpace = 0; // UI overlays on bottom
    const sideMargin = 0; // Use full width

    const availableWidth = canvasWidth - (sideMargin * 2);
    const availableHeight = canvasHeight - uiTopSpace - uiBottomSpace;

    // Calculate maximum block size that fits
    const maxBlockWidth = Math.floor(availableWidth / this.GRID_WIDTH);
    const maxBlockHeight = Math.floor(availableHeight / this.GRID_HEIGHT);

    // Use the smaller of the two to ensure it fits
    this.BLOCK_SIZE = Math.min(maxBlockWidth, maxBlockHeight);

    // Center the grid
    const gridPixelWidth = this.GRID_WIDTH * this.BLOCK_SIZE;
    const gridPixelHeight = this.GRID_HEIGHT * this.BLOCK_SIZE;

    this.GRID_OFFSET_X = Math.floor((canvasWidth - gridPixelWidth) / 2);
    this.GRID_OFFSET_Y = Math.floor((canvasHeight - gridPixelHeight) / 2);

    console.log(`Block Drop sizing: canvas=${canvasWidth}x${canvasHeight}, blockSize=${this.BLOCK_SIZE}, offset=${this.GRID_OFFSET_X},${this.GRID_OFFSET_Y}`);
  }

  public init(): void {
    console.log('Block Drop Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 1; // Block Drop is typically single-life
    this.level = 1;
    this.lines = 0;
    this.isGameOver = false;
    
    // Reset timers
    this.dropTimer = 0;
    this.lockDelayTimer = 0;
    this.keyRepeatTimers = {};
    
    // Initialize grid and pieces
    this.grid.clear();
    this.pieceGenerator.reset();
    
    this.currentPiece = null;
    this.nextPiece = this.pieceGenerator.getNextPiece();
    
    // Reset statistics
    Object.keys(this.pieceStats).forEach(key => {
      this.pieceStats[key as PieceType] = 0;
    });
    
    this.spawnNewPiece();
    
    // Set initial drop speed
    this.updateDropSpeed();
  }

  private spawnNewPiece(): void {
    if (!this.nextPiece) {
      this.nextPiece = this.pieceGenerator.getNextPiece();
    }
    
    this.currentPiece = this.nextPiece;
    this.currentPiece.setPosition(
      Math.floor(this.GRID_WIDTH / 2) - Math.floor(this.currentPiece.getSize() / 2),
      0
    );
    
    // Update statistics
    this.pieceStats[this.currentPiece.getType()]++;
    
    this.nextPiece = this.pieceGenerator.getNextPiece();
    this.lockDelayTimer = 0;
    
    // Check for game over - if piece can't be placed at spawn
    if (this.grid.checkCollision(this.currentPiece)) {
      this.gameOver();
    }
  }

  private updateDropSpeed(): void {
    // Drop speed increases with level (classic Tetris formula)
    const baseSpeed = 1.0;
    this.dropInterval = Math.max(0.05, baseSpeed - (this.level - 1) * 0.08);
  }

  public update(deltaTime: number): void {
    if (this.isGameOver || !this.currentPiece) return;
    
    // Update key repeat timers
    Object.keys(this.keyRepeatTimers).forEach(key => {
      this.keyRepeatTimers[key] -= deltaTime;
    });
    
    // Update drop timer
    this.dropTimer += deltaTime;
    
    // Handle piece dropping
    const dropSpeed = this.softDropTimer > 0 ? this.SOFT_DROP_SPEED : this.dropInterval;

    if (this.dropTimer >= dropSpeed) {
      this.dropTimer = 0;

      if (this.canMovePiece(0, 1)) {
        this.currentPiece.move(0, 1);
        this.lockDelayTimer = 0; // Reset lock delay when piece moves down
      } else {
        // Piece has landed - increment lock delay
        this.lockDelayTimer += dropSpeed;

        if (this.lockDelayTimer >= this.LOCK_DELAY_DURATION) {
          this.lockPiece();
        }
      }
    }
    
    // Update soft drop timer
    if (this.softDropTimer > 0) {
      this.softDropTimer -= deltaTime;
    }
  }

  private canMovePiece(deltaX: number, deltaY: number): boolean {
    if (!this.currentPiece) return false;
    
    const testPiece = this.currentPiece.clone();
    testPiece.move(deltaX, deltaY);
    
    return !this.grid.checkCollision(testPiece);
  }

  private tryRotatePiece(): boolean {
    if (!this.currentPiece) return false;
    
    const testPiece = this.currentPiece.clone();
    testPiece.rotate();
    
    // Try basic rotation
    if (!this.grid.checkCollision(testPiece)) {
      // Rotation is valid, apply it to the actual piece
      this.currentPiece.rotate();
      return true;
    }
    
    // Try wall kicks (Simple SRS-style kicks)
    const kicks = [
      { x: 1, y: 0 },   // Right
      { x: -1, y: 0 },  // Left
      { x: 0, y: -1 },  // Up
      { x: 1, y: -1 },  // Right-up
      { x: -1, y: -1 }  // Left-up
    ];
    
    for (const kick of kicks) {
      const kickTestPiece = this.currentPiece.clone();
      kickTestPiece.rotate();
      kickTestPiece.move(kick.x, kick.y);
      
      if (!this.grid.checkCollision(kickTestPiece)) {
        // Apply the kick to the actual piece
        this.currentPiece.rotate();
        this.currentPiece.move(kick.x, kick.y);
        return true;
      }
    }
    
    return false;
  }

  private lockPiece(): void {
    if (!this.currentPiece) return;
    
    // Place piece on grid
    this.grid.placePiece(this.currentPiece);
    this.audioManager.playExplosion(); // Lock sound
    
    // Check for line clears
    const linesCleared = this.grid.clearLines();
    
    if (linesCleared > 0) {
      this.handleLineClear(linesCleared);
      this.audioManager.playPowerUp(); // Line clear sound
    }
    
    // Spawn new piece
    this.spawnNewPiece();
  }

  private handleLineClear(linesCleared: number): void {
    this.lines += linesCleared;
    
    // Nintendo NES Tetris authentic scoring: basePoints × (level + 1)
    let basePoints = 0;
    switch (linesCleared) {
      case 1: basePoints = this.LINE_CLEAR_BASE_POINTS.SINGLE; break;
      case 2: basePoints = this.LINE_CLEAR_BASE_POINTS.DOUBLE; break;
      case 3: basePoints = this.LINE_CLEAR_BASE_POINTS.TRIPLE; break;
      case 4: basePoints = this.LINE_CLEAR_BASE_POINTS.TETRIS; break;
    }
    
    // Score = base points × (level + 1) - authentic NES formula
    this.addScore(basePoints * (this.level + 1));
    
    // Level progression (every 10 lines) - authentic NES progression
    const newLevel = Math.floor(this.lines / 10) + 1;
    if (newLevel > this.level) {
      this.level = newLevel;
      this.updateDropSpeed();
      console.log(`Level up! Now level ${this.level}`);
    }
  }


  private hardDrop(): void {
    if (!this.currentPiece) return;
    
    let dropDistance = 0;
    
    // Drop piece as far as possible
    while (this.canMovePiece(0, 1)) {
      this.currentPiece.move(0, 1);
      dropDistance++;
    }
    
    // Award hard drop points
    this.addScore(dropDistance * this.DROP_POINTS.HARD_DROP);
    
    // Immediately lock piece
    this.lockDelayTimer = this.LOCK_DELAY_DURATION;
    this.lockPiece();
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Draw grid background
    this.renderGridBackground();
    
    // Render placed blocks
    this.grid.render(this.GRID_OFFSET_X, this.GRID_OFFSET_Y, this.BLOCK_SIZE);
    
    // Render current piece
    if (this.currentPiece) {
      this.renderPiece(this.currentPiece, this.GRID_OFFSET_X, this.GRID_OFFSET_Y, this.BLOCK_SIZE, false);
      
      // Render ghost piece (preview of where piece will land)
      this.renderGhostPiece();
    }
    
    // Render UI
    this.renderUI();
    
    // Render game over screen
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  private renderGridBackground(): void {
    // Draw grid border
    this.renderManager.strokeRect(
      this.GRID_OFFSET_X - 2,
      this.GRID_OFFSET_Y - 2,
      this.GRID_WIDTH * this.BLOCK_SIZE + 4,
      this.GRID_HEIGHT * this.BLOCK_SIZE + 4,
      '#666666',
      2
    );
    
    // Draw grid lines
    for (let x = 0; x <= this.GRID_WIDTH; x++) {
      this.renderManager.drawLine(
        this.GRID_OFFSET_X + x * this.BLOCK_SIZE,
        this.GRID_OFFSET_Y,
        this.GRID_OFFSET_X + x * this.BLOCK_SIZE,
        this.GRID_OFFSET_Y + this.GRID_HEIGHT * this.BLOCK_SIZE,
        '#333333',
        1
      );
    }
    
    for (let y = 0; y <= this.GRID_HEIGHT; y++) {
      this.renderManager.drawLine(
        this.GRID_OFFSET_X,
        this.GRID_OFFSET_Y + y * this.BLOCK_SIZE,
        this.GRID_OFFSET_X + this.GRID_WIDTH * this.BLOCK_SIZE,
        this.GRID_OFFSET_Y + y * this.BLOCK_SIZE,
        '#333333',
        1
      );
    }
  }

  private renderPiece(piece: Piece, offsetX: number, offsetY: number, blockSize: number, isGhost: boolean): void {
    const shape = piece.getShape();
    const position = piece.getPosition();
    const color = isGhost ? '#333333' : piece.getColor();
    
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const blockX = offsetX + (position.x + x) * blockSize;
          const blockY = offsetY + (position.y + y) * blockSize;
          
          if (isGhost) {
            this.renderManager.strokeRect(
              blockX + 1,
              blockY + 1,
              blockSize - 2,
              blockSize - 2,
              color,
              1
            );
          } else {
            this.renderManager.fillRect(
              blockX + 1,
              blockY + 1,
              blockSize - 2,
              blockSize - 2,
              color
            );
            
            // Add highlight for 3D effect
            this.renderManager.strokeRect(
              blockX + 1,
              blockY + 1,
              blockSize - 2,
              blockSize - 2,
              '#FFFFFF',
              1
            );
          }
        }
      }
    }
  }

  private renderGhostPiece(): void {
    if (!this.currentPiece) return;
    
    const ghostPiece = this.currentPiece.clone();
    
    // Drop ghost piece to bottom
    while (!this.grid.checkCollision(ghostPiece)) {
      ghostPiece.move(0, 1);
    }
    ghostPiece.move(0, -1); // Move back up one step
    
    // Only render if ghost is below current piece
    if (ghostPiece.getPosition().y > this.currentPiece.getPosition().y) {
      this.renderPiece(ghostPiece, this.GRID_OFFSET_X, this.GRID_OFFSET_Y, this.BLOCK_SIZE, true);
    }
  }

  private renderUI(): void {
    const canvas = this.renderManager.getCanvas();
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const fontSize = Math.max(10, Math.min(14, canvasWidth * 0.022));
    const smallFont = `${fontSize}px "Courier New", monospace`;

    // Semi-transparent background for UI bars so they're readable over the game
    const barHeight = 20;

    // TOP BAR with background
    this.renderManager.fillRect(0, 0, canvasWidth, barHeight, 'rgba(0, 0, 0, 0.7)');

    const topY = 14;
    this.renderManager.drawText(
      `SC:${this.score.toString().padStart(6, '0')}`,
      8,
      topY,
      '#FFFFFF',
      smallFont
    );

    const centerX = canvasWidth / 2;
    this.renderManager.drawText(
      `LV:${this.level} LN:${this.lines}`,
      centerX - 50,
      topY,
      '#FFFFFF',
      smallFont
    );

    // Next piece - show compact in top right
    if (this.nextPiece) {
      const nextX = canvasWidth - 75;
      this.renderManager.drawText(
        'NXT:',
        nextX,
        topY,
        '#FFFFFF',
        smallFont
      );
      const previewBlockSize = Math.max(6, Math.min(10, this.BLOCK_SIZE * 0.35));
      this.renderPiece(this.nextPiece, nextX + 28, 2, previewBlockSize, false);
    }

    // BOTTOM BAR with background
    this.renderManager.fillRect(0, canvasHeight - barHeight, canvasWidth, barHeight, 'rgba(0, 0, 0, 0.7)');

    const bottomY = canvasHeight - 6;
    const controlFont = `${Math.max(8, fontSize - 3)}px "Courier New", monospace`;

    this.renderManager.drawText(
      '←→:Move ↓:Drop SPC:Hard Z/X:Rotate',
      8,
      bottomY,
      '#CCCCCC',
      controlFont
    );
  }

  protected renderGameOver(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      'GAME OVER',
      centerX,
      centerY - 40,
      '#FFFFFF',
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `FINAL SCORE: ${this.score}`,
      centerX,
      centerY,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `LEVEL ${this.level} - ${this.lines} LINES`,
      centerX,
      centerY + 30,
      '#CCCCCC',
      '16px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'PRESS SPACE TO RESTART',
      centerX,
      centerY + 80,
      '#FFFFFF',
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }

    if (!this.currentPiece) return;

    // Movement (with key repeat)
    if ((input.keys['ArrowLeft'] || input.keys['KeyA']) && this.canRepeatKey('left')) {
      if (this.canMovePiece(-1, 0)) {
        this.currentPiece.move(-1, 0);
        this.resetLockDelay();
      }
    }

    if ((input.keys['ArrowRight'] || input.keys['KeyD']) && this.canRepeatKey('right')) {
      if (this.canMovePiece(1, 0)) {
        this.currentPiece.move(1, 0);
        this.resetLockDelay();
      }
    }

    // Soft drop
    if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      this.softDropTimer = this.SOFT_DROP_SPEED;
      this.addScore(this.DROP_POINTS.SOFT_DROP);
    }

    // Rotation
    if ((input.keys['KeyZ'] || input.keys['KeyX'] || input.keys['ArrowUp']) && this.canRepeatKey('rotate')) {
      if (this.tryRotatePiece()) {
        this.resetLockDelay();
      }
    }

    // Hard drop
    if (input.keys['Space'] && this.canRepeatKey('space')) {
      this.hardDrop();
    }
  }

  private canRepeatKey(keyName: string): boolean {
    if (!this.keyRepeatTimers[keyName] || this.keyRepeatTimers[keyName] <= 0) {
      this.keyRepeatTimers[keyName] = this.KEY_REPEAT_DELAY;
      return true;
    }
    return false;
  }

  private resetLockDelay(): void {
    this.lockDelayTimer = 0;
  }

  public cleanup(): void {
    super.cleanup();
    this.currentPiece = null;
    this.nextPiece = null;
  }
}